import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import withAccountFormStepStyles from 'Components/UI/Accounts/AccountApplication/Step/withAccountFormStepStyles';
import MetaField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import classNames from 'classnames';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import Typography from 'Components/UI/Typography';
import { Divider } from '@material-ui/core';
import { Collapse } from '@mui/material';
import PhoneNumberField from 'Components/UI/Accounts/AccountApplication/Step/Fields/PhoneNumberField';

const hideSecondary = resource => {
  return resource
    .meta()
    .field('secondaryApplicantPhoneNumber')
    .attr('hidden');
};


function ContactInformationWithMailingAddress({ classes }) {
  const { resource, handleChange } = useMetaLayoutContext();
  const isSecondaryHidden = hideSecondary(resource);
  const isCustodial = resource.attr('isCustodial') === 'true';
  const sameAsPrimary = resource.attr('sameContactInfoAsPrimary') === 'true';
  const primaryMailingAddressSameAsPhysical = resource.attr('primaryApplicantMailingAddressSameAsPhysicalAddress') === 'true';
  const secondaryMailingAddressSameAsPhysical = resource.attr('secondaryApplicantMailingAddressSameAsPhysicalAddress') === 'true';

  useEffect(() => {
    if (primaryMailingAddressSameAsPhysical) {
      handleChange('primaryApplicantMailingAddressLine1', resource.attr('primaryApplicantAddressLine1'));
      handleChange('primaryApplicantMailingAddressLine2', resource.attr('primaryApplicantAddressLine2'));
      handleChange('primaryApplicantMailingAddressCity', resource.attr('primaryApplicantCity'));
      handleChange('primaryApplicantMailingAddressState', resource.attr('primaryApplicantState'));
      handleChange('primaryApplicantMailingAddressZipCode', resource.attr('primaryApplicantZipCode'));
    }
    if (sameAsPrimary) {
      handleChange('secondaryApplicantAddressLine1', resource.attr('primaryApplicantAddressLine1'));
      handleChange('secondaryApplicantAddressLine2', resource.attr('primaryApplicantAddressLine2'));
      handleChange('secondaryApplicantCity', resource.attr('primaryApplicantCity'));
      handleChange('secondaryApplicantState', resource.attr('primaryApplicantState'));
      handleChange('secondaryApplicantZipCode', resource.attr('primaryApplicantZipCode'));
      handleChange('secondaryApplicantMailingAddressLine1', resource.attr('primaryApplicantMailingAddressLine1'));
      handleChange('secondaryApplicantMailingAddressLine2', resource.attr('primaryApplicantMailingAddressLine2'));
      handleChange('secondaryApplicantMailingAddressCity', resource.attr('primaryApplicantMailingAddressCity'));
      handleChange('secondaryApplicantMailingAddressState', resource.attr('primaryApplicantMailingAddressState'));
      handleChange('secondaryApplicantMailingAddressZipCode', resource.attr('primaryApplicantMailingAddressZipCode'));
    }
    if (!sameAsPrimary && secondaryMailingAddressSameAsPhysical) {
      handleChange('secondaryApplicantMailingAddressLine1', resource.attr('secondaryApplicantAddressLine1'));
      handleChange('secondaryApplicantMailingAddressLine2', resource.attr('secondaryApplicantAddressLine2'));
      handleChange('secondaryApplicantMailingAddressCity', resource.attr('secondaryApplicantCity'));
      handleChange('secondaryApplicantMailingAddressState', resource.attr('secondaryApplicantState'));
      handleChange('secondaryApplicantMailingAddressZipCode', resource.attr('secondaryApplicantZipCode'));
    }
  }, [primaryMailingAddressSameAsPhysical, sameAsPrimary, secondaryMailingAddressSameAsPhysical, resource]);


  return (
    <div className={classes.root}>
      <div className={classes.row}>
        <Typography
          className={classes.header}
          variant="h3"
          color="primary"
          component={'h2'}
        >
          {`Primary - ${resource.attr('primaryApplicantName')}`}
        </Typography>
      </div>
      <div className={classes.row}>
        <PhoneNumberField attr="primaryApplicantPhoneNumber" />
        <MetaField
          attr="primaryApplicantPhoneNumberType"
        />
      </div>
      <Typography
        className={classes.subHeader}
        variant="h3"
        color="primary"
        component={'h2'}
      >
        {resource.attr('physicalAddressTitle')}
      </Typography>
      <div className={classes.row}>
        <MetaField
          attr="primaryApplicantAddressLine1"
        />
        <MetaField
          attr="primaryApplicantAddressLine2"
        />
      </div>
      <div className={classes.row}>
        <MetaField attr="primaryApplicantCity" />
        <MetaField attr="primaryApplicantState" />
      </div>
      <div className={classes.row}>
        <MetaField attr="primaryApplicantZipCode" />
        <MetaField attr="primaryApplicantCountry" />
      </div>
      <MetaField
        attr="primaryApplicantMailingAddressSameAsPhysicalAddress"
        label={resource.attr('primarySameMailingAsPhysicalAddressTitle')}
        onChange={(primaryMailingAddressIsSame) => {
          if (primaryMailingAddressIsSame) {
            handleChange('primaryApplicantMailingAddressLine1', resource.attr('primaryApplicantAddressLine1'));
            handleChange('primaryApplicantMailingAddressLine2', resource.attr('primaryApplicantAddressLine2'));
            handleChange('primaryApplicantMailingAddressCity', resource.attr('primaryApplicantCity'));
            handleChange('primaryApplicantMailingAddressState', resource.attr('primaryApplicantState'));
            handleChange('primaryApplicantMailingAddressZipCode', resource.attr('primaryApplicantZipCode'));
          } else {
            handleChange('primaryApplicantMailingAddressLine1', '');
            handleChange('primaryApplicantMailingAddressLine2', '');
            handleChange('primaryApplicantMailingAddressCity', '');
            handleChange('primaryApplicantMailingAddressState', '');
            handleChange('primaryApplicantMailingAddressZipCode', '');
          }
        }}
      />
      <Collapse in={!primaryMailingAddressSameAsPhysical}>
        <div className={classes.collapseRoot}>
          <Typography
            className={classes.subHeader}
            variant="h3"
            color="primary"
            component={'h2'}
          >
            {resource.attr('mailingAddressTitle')}
          </Typography>
          <div className={classes.row}>
            <MetaField attr="primaryApplicantMailingAddressLine1" />
            <MetaField attr="primaryApplicantMailingAddressLine2" />
          </div>
          <div className={classes.row}>
            <MetaField attr="primaryApplicantMailingAddressCity" />
            <MetaField attr="primaryApplicantMailingAddressState" />
          </div>
          <div className={classes.row}>
            <MetaField attr="primaryApplicantMailingAddressZipCode" />
            <MetaField attr="primaryApplicantMailingAddressCountry" />
          </div>
        </div>
      </Collapse>
      { !isSecondaryHidden
        ? (
          <div>
            <Divider style={{ marginBottom: 16 }} />
            <div
              className={classes.row}
            >
              <Typography
                className={classes.header}
                variant="h3"
                color="primary"
                component={'h2'}
              >
                {`${isCustodial ? 'Dependent' : 'Secondary'} - ${resource.attr('secondaryApplicantName')}`}
              </Typography>
              <MetaField
                className={classes.sameAsPrimary}
                attr="sameContactInfoAsPrimary"
                label={`Same as ${isCustodial ? '' : 'address as '}${resource.attr('primaryApplicantName')}?`}
                onChange={(sameContactInfo) => {
                  if (sameContactInfo) {
                    if (isCustodial) {
                      handleChange('secondaryApplicantPhoneNumber', resource.attr('primaryApplicantPhoneNumber'));
                      handleChange('secondaryApplicantPhoneNumberType', resource.attr('primaryApplicantPhoneNumberType'));
                    }

                    handleChange('secondaryApplicantAddressLine1', resource.attr('primaryApplicantAddressLine1'));
                    handleChange('secondaryApplicantAddressLine2', resource.attr('primaryApplicantAddressLine2'));
                    handleChange('secondaryApplicantCity', resource.attr('primaryApplicantCity'));
                    handleChange('secondaryApplicantState', resource.attr('primaryApplicantState'));
                    handleChange('secondaryApplicantZipCode', resource.attr('primaryApplicantZipCode'));
                    handleChange('secondaryApplicantMailingAddressLine1', resource.attr('primaryApplicantMailingAddressLine1'));
                    handleChange('secondaryApplicantMailingAddressLine2', resource.attr('primaryApplicantMailingAddressLine2'));
                    handleChange('secondaryApplicantMailingAddressCity', resource.attr('primaryApplicantMailingAddressCity'));
                    handleChange('secondaryApplicantMailingAddressState', resource.attr('primaryApplicantMailingAddressState'));
                    handleChange('secondaryApplicantMailingAddressZipCode', resource.attr('primaryApplicantMailingAddressZipCode'));
                  } else {
                    if (isCustodial) {
                      handleChange('secondaryApplicantPhoneNumber', '');
                      handleChange('secondaryApplicantPhoneNumberType', '');
                    }

                    handleChange('secondaryApplicantAddressLine1', '');
                    handleChange('secondaryApplicantAddressLine2', '');
                    handleChange('secondaryApplicantCity', '');
                    handleChange('secondaryApplicantState', '');
                    handleChange('secondaryApplicantZipCode', '');
                    handleChange('secondaryApplicantMailingAddressLine1', '');
                    handleChange('secondaryApplicantMailingAddressLine2', '');
                    handleChange('secondaryApplicantMailingAddressCity', '');
                    handleChange('secondaryApplicantMailingAddressState', '');
                    handleChange('secondaryApplicantMailingAddressZipCode', '');
                  }
                }}
              />
            </div>
            <div className={classNames(classes.row, {
              [classes.hidden]: isCustodial && sameAsPrimary,
            })}
            >
              <PhoneNumberField attr="secondaryApplicantPhoneNumber" />
              <MetaField attr="secondaryApplicantPhoneNumberType" />
            </div>
            <Collapse in={!sameAsPrimary}>
              <div className={classes.collapseRoot}>
                <Typography
                  className={classes.subHeader}
                  variant="h3"
                  color="primary"
                  component={'h2'}
                >
                  {resource.attr('physicalAddressTitle')}
                </Typography>
                <div className={classes.row}>
                  <MetaField attr="secondaryApplicantAddressLine1" />
                  <MetaField attr="secondaryApplicantAddressLine2" />
                </div>
                <div className={classes.row}>
                  <MetaField attr="secondaryApplicantCity" />
                  <MetaField attr="secondaryApplicantState" />
                </div>
                <div className={classes.row}>
                  <MetaField attr="secondaryApplicantZipCode" />
                  <MetaField attr="secondaryApplicantCountry" />
                </div>
                <MetaField
                  attr="secondaryApplicantMailingAddressSameAsPhysicalAddress"
                  label={resource.attr('secondarySameMailingAsPhysicalAddressTitle')}
                  onChange={(secondaryMailingAddressIsSame) => {
                    if (secondaryMailingAddressIsSame) {
                      handleChange('secondaryApplicantMailingAddressLine1', resource.attr('secondaryApplicantAddressLine1'));
                      handleChange('secondaryApplicantMailingAddressLine2', resource.attr('secondaryApplicantAddressLine2'));
                      handleChange('secondaryApplicantMailingAddressCity', resource.attr('secondaryApplicantCity'));
                      handleChange('secondaryApplicantMailingAddressState', resource.attr('secondaryApplicantState'));
                      handleChange('secondaryApplicantMailingAddressZipCode', resource.attr('secondaryApplicantZipCode'));
                    } else {
                      handleChange('secondaryApplicantMailingAddressLine1', '');
                      handleChange('secondaryApplicantMailingAddressLine2', '');
                      handleChange('secondaryApplicantMailingAddressCity', '');
                      handleChange('secondaryApplicantMailingAddressState', '');
                      handleChange('secondaryApplicantMailingAddressZipCode', '');
                    }
                  }}
                />
              </div>
            </Collapse>
            <Collapse in={!sameAsPrimary && !secondaryMailingAddressSameAsPhysical}>
              <div className={classes.collapseRoot}>
                <Typography
                  className={classes.subHeader}
                  variant="h3"
                  color="primary"
                  component={'h2'}
                >
                  {resource.attr('mailingAddressTitle')}
                </Typography>
                <div className={classes.row}>
                  <MetaField attr="secondaryApplicantMailingAddressLine1" />
                  <MetaField attr="secondaryApplicantMailingAddressLine2" />
                </div>
                <div className={classes.row}>
                  <MetaField attr="secondaryApplicantMailingAddressCity" />
                  <MetaField attr="secondaryApplicantMailingAddressState" />
                </div>
                <div className={classes.row}>
                  <MetaField attr="secondaryApplicantMailingAddressZipCode" />
                  <MetaField attr="secondaryApplicantMailingAddressCountry" />
                </div>
              </div>
            </Collapse>
          </div>) : null}
    </div>
  );
}

ContactInformationWithMailingAddress.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withAccountFormStepStyles(ContactInformationWithMailingAddress);
